import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { format, parseISO } from "date-fns";
import { Calendar, Clock, MapPin, Phone, Mail } from "lucide-react";

type AuctionPost = Tables<"auction_posts">;
type TenantSettings = Pick<
  Tables<"tenant_settings">,
  "auctioneer_name" | "auctioneer_phone" | "auctioneer_email"
>;

interface AuctionPostDisplayProps {
  post: AuctionPost;
  tenant: TenantSettings;
}

export function AuctionPostDisplay({ post, tenant }: AuctionPostDisplayProps) {
  const [images, setImages] = useState<{ id: string; url: string }[]>([]);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("auction_post_images")
        .select("id, storage_path, display_order")
        .eq("post_id", post.id)
        .order("display_order");

      setImages(
        (data ?? []).map((img) => ({
          id: img.id,
          url: supabase.storage
            .from("auction-post-images")
            .getPublicUrl(img.storage_path).data.publicUrl,
        })),
      );
    };
    load();
  }, [post.id]);

  const dateLabel = format(parseISO(post.auction_date), "EEEE, MMMM d, yyyy");
  const timeLabel = formatTime(post.auction_time);

  return (
    <Card className="overflow-hidden">
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 bg-muted">
          {images.slice(0, 8).map((img) => (
            <a
              key={img.id}
              href={img.url}
              target="_blank"
              rel="noopener noreferrer"
              className="aspect-square overflow-hidden"
            >
              <img
                src={img.url}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover hover:opacity-90 transition-smooth"
              />
            </a>
          ))}
          {images.length > 8 && (
            <div className="aspect-square bg-muted flex items-center justify-center text-sm text-muted-foreground">
              +{images.length - 8} more
            </div>
          )}
        </div>
      )}

      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">{post.title}</h2>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {dateLabel}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {timeLabel}
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              {post.location}
            </span>
          </div>
        </div>

        {post.items_description && (
          <Section heading="Items">
            <p className="whitespace-pre-wrap text-foreground/90">{post.items_description}</p>
          </Section>
        )}

        {post.terms && (
          <Section heading="Payment terms">
            <p className="whitespace-pre-wrap text-foreground/90">{post.terms}</p>
          </Section>
        )}

        {post.auctioneer_notes && (
          <Section heading="Auctioneer notes">
            <p className="whitespace-pre-wrap text-foreground/90">{post.auctioneer_notes}</p>
          </Section>
        )}

        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Contact
          </h3>
          <div className="space-y-1 text-sm">
            {tenant.auctioneer_name && <p className="font-medium">{tenant.auctioneer_name}</p>}
            {tenant.auctioneer_phone && (
              <a
                href={`tel:${tenant.auctioneer_phone}`}
                className="flex items-center gap-1.5 text-primary hover:underline"
              >
                <Phone className="w-4 h-4" />
                {tenant.auctioneer_phone}
              </a>
            )}
            {tenant.auctioneer_email && (
              <a
                href={`mailto:${tenant.auctioneer_email}`}
                className="flex items-center gap-1.5 text-primary hover:underline"
              >
                <Mail className="w-4 h-4" />
                {tenant.auctioneer_email}
              </a>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        {heading}
      </h3>
      {children}
    </div>
  );
}

// Postgres `time` comes back as "HH:MM:SS". Format to 12-hour w/ AM/PM.
function formatTime(pgTime: string): string {
  const [hhStr, mmStr] = pgTime.split(":");
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return pgTime;
  const date = new Date();
  date.setHours(hh, mm, 0, 0);
  return format(date, "h:mm a");
}
